import Button from "@components/Button"
import Spinner from "@components/Spinner"
import { pollPaymentIntent } from "@helpers/pollPaymentIntent"
import { useStripe } from "@stripe/react-stripe-js"
import { PaymentIntent } from "@stripe/stripe-js"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

interface Props {
  clientSecret: string
  /** The PaymentIntent, once it is no longer pending. */
  onSettled: (paymentIntent: PaymentIntent) => void
}

export default function PaymentPending({ clientSecret, onSettled }: Props) {
  const [attempt, setAttempt] = useState<number>(0)
  const [isTimedOut, setIsTimedOut] = useState<boolean>(false)

  // NOTE: A ref, so a new callback each render doesn't restart the polling.
  const onSettledRef = useRef(onSettled)
  const stripe = useStripe()
  const { t } = useTranslation()

  useEffect(() => {
    onSettledRef.current = onSettled
  })

  useEffect(() => {
    if (!stripe) return

    const controller = new AbortController()

    pollPaymentIntent({
      retrieve: async () =>
        (await stripe.retrievePaymentIntent(clientSecret)).paymentIntent,
      signal: controller.signal,
    })
      .then((paymentIntent) => {
        if (controller.signal.aborted) return

        if (paymentIntent) onSettledRef.current(paymentIntent)
        else setIsTimedOut(true)
      })
      .catch(() => setIsTimedOut(true))

    return () => controller.abort()
  }, [attempt, clientSecret, stripe])

  return (
    <div
      aria-live="polite"
      className="vm-card flex animate-fade-up flex-col items-center gap-4 p-6 text-center"
    >
      {!isTimedOut && <Spinner />}

      <h2 className="text-lg font-semibold">
        {t("components.paymentPending.heading")}
      </h2>

      <p className="text-sm text-vm-muted">
        {t(
          isTimedOut
            ? "components.paymentPending.timedOutText"
            : "components.paymentPending.text",
        )}
      </p>

      {isTimedOut && (
        <Button
          onClick={() => {
            setIsTimedOut(false)
            setAttempt((count) => count + 1)
          }}
          title={t("components.paymentPending.checkAgain")}
          variant="secondary"
        />
      )}
    </div>
  )
}
